import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/api";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion, AnimatePresence } from "framer-motion";
import CustomSelect from "../components/CustomSelect";
import EspecialidadeTagSelect from "../components/EspecialidadeTagSelect";
import { getEspecialidadesPorEstabelecimento } from "../api/especialidades";
import useAutoRefresh from "../hooks/useAutoRefresh";
import { buildPaginationItems } from "../utils/pagination";
import { useProfissionalRotulo } from "../contexts/ProfissionalRotuloContext";
import { getProfissionalLabels } from "../utils/profissionalRotulo";
import {
  Stethoscope,
  Search,
  X,
  Edit,
  Trash2,
  Plus,
  Save,
  ToggleRight,
  ToggleLeft,
} from "lucide-react";

const Profissionais = () => {
  // Toggle to show/hide especialidades field across create/edit
  const SHOW_ESPECIALIDADES = false;
  // Toggle to show/hide foto upload field
  const SHOW_IMAGEM = false;

  const formatTelefone = (value) => {
    if (!value && value !== 0) return "";
    const digits = String(value).replace(/\D/g, "");
    if (!digits) return "";
    if (digits.length <= 10) {
      // (XX) XXXX-XXXX
      return digits.replace(/^(\d{0,2})(\d{0,4})(\d{0,4}).*$/, (m, g1, g2, g3) => {
        let out = "";
        if (g1) out += `(${g1}) `;
        if (g2) out += g2;
        if (g3) out += g3 ? `-${g3}` : "";
        return out.trim();
      });
    }
    // (XX) XXXXX-XXXX for 11+ digits
    return digits.replace(/^(\d{0,2})(\d{0,5})(\d{0,4}).*$/, (m, g1, g2, g3) => {
      let out = "";
      if (g1) out += `(${g1}) `;
      if (g2) out += g2;
      if (g3) out += g3 ? `-${g3}` : "";
      return out.trim();
    });
  };

  const getInitials = (name = "") =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "P";

  const isAtivo = (item) => item?.ativo === true || item?.ativo === 1 || item?.ativo === "1";
  // Funções do Drawer e ações
  const abrirDrawer = (profissional) => {
    // Corrige especialidades para sempre ser array
    let especialidadesArr = profissional.especialidades;
    if (typeof especialidadesArr === 'string') {
      try {
        especialidadesArr = JSON.parse(especialidadesArr);
      } catch {
        especialidadesArr = [];
      }
    }
    if (!Array.isArray(especialidadesArr)) especialidadesArr = [];
    // Não sobrescreve o campo foto, apenas trata para exibição
    let fotoProfissional = profissional.foto;
    if (typeof fotoProfissional === 'string') {
      fotoProfissional = fotoProfissional.replace(/\\/g, '/');
    }
    setProfissionalSelecionado({
      ...profissional,
      especialidades: especialidadesArr,
      telefone:
        profissional.telefone && profissional.telefone !== "undefined"
          ? formatTelefone(profissional.telefone)
          : "",
    });
    setDrawerOpen(true);
  };

  const fecharDrawer = () => {
    setDrawerOpen(false);
    setProfissionalSelecionado(null);
  };

  const deletarProfissional = async (id) => {
    try {
      await api.delete(`/profissionais/${id}`);
      toast.success(`${rotulosProfissional.singular} ${rotulosProfissional.excluido}!`);
      carregarProfissionais();
      fecharDrawer();
    } catch (err) {
      console.error('Erro ao excluir profissional:', err);
      toast.error(err.response?.data?.mensagem || `Erro ao excluir ${rotulosProfissional.singularLower}!`);
    }
  };

  const confirmarExcluirProfissional = (id, nome) => {
    const nomeExibicao = (nome || '').toString().toUpperCase();
    Swal.fire({
      title: 'Confirma exclusão?',
      html: `Deseja realmente excluir ${rotulosProfissional.artigo} ${rotulosProfissional.singularLower} <strong>${nomeExibicao}</strong>? Esta ação não pode ser desfeita.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
    }).then((result) => {
      if (result.isConfirmed) {
        deletarProfissional(id);
      }
    });
  };

  const salvarAlteracoes = async () => {
    if (!profissionalSelecionado) return;
    try {
      const telefoneDigits = profissionalSelecionado.telefone
        ? String(profissionalSelecionado.telefone).replace(/\D/g, "")
        : "";
      const payload = {
        nome: profissionalSelecionado.nome,
        telefone: telefoneDigits,
        admin: !!profissionalSelecionado.admin,
        especialidades: Array.isArray(profissionalSelecionado.especialidades)
          ? profissionalSelecionado.especialidades
          : [],
        ativo: !!profissionalSelecionado.ativo,
        estabelecimento_id: profissionalSelecionado.estabelecimento_id,
      };
      const { data } = await api.put(`/profissionais/${profissionalSelecionado.id}`, payload);
      toast.success(`${rotulosProfissional.singular} ${rotulosProfissional.atualizado} com sucesso!`);
      if (data?.adminAnterior?.length) {
        const nomes = data.adminAnterior.map((a) => a.nome).filter(Boolean).join(', ');
        toast.info(`Admin anterior desmarcado: ${nomes || `${rotulosProfissional.outro} ${rotulosProfissional.singularLower}`}`);
      }
      carregarProfissionais();
      fecharDrawer();
    } catch (err) {
      console.error('Erro ao atualizar profissional:', err);
      toast.error(`Erro ao atualizar ${rotulosProfissional.singularLower}!`);
    }
  };

  // Estado
  const [profissionais, setProfissionais] = useState([]);
  const [busca, setBusca] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("todos");
  const [loading, setLoading] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profissionalSelecionado, setProfissionalSelecionado] = useState(null);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const { labels: labelsContexto, carregando: carregandoRotulo } = useProfissionalRotulo();
  const rotulosProfissional =
    labelsContexto ||
    (!carregandoRotulo ? getProfissionalLabels({}) : null) ||
    getProfissionalLabels({});

  // Modal Novo Profissional
  const [showNovo, setShowNovo] = useState(false);
  const [novoLoading, setNovoLoading] = useState(false);
  const [optsLoading, setOptsLoading] = useState(false);
  const [estabelecimentos, setEstabelecimentos] = useState([]);
const [novoProfissional, setNovoProfissional] = useState({
  nome: "",
  telefone: "",
  admin: false,
  estabelecimento_id: "",
  especialidades: [],
  foto: null,
  ativo: true,
});
  const [erros, setErros] = useState({});
    const [especialidades, setEspecialidades] = useState([]);

  // Spinner
  const Spinner = ({ size = 16 }) => (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        border: "2px solid rgba(0,0,0,0.15)",
        borderTopColor: "var(--primary)",
        borderRadius: "50%",
        animation: "spin 0.6s linear infinite",
      }}
    />
  );

  // CSS keyframes
  useEffect(() => {
    if (document.getElementById("spin-keyframes")) return;
    const style = document.createElement("style");
    style.id = "spin-keyframes";
    style.innerHTML = `
      @keyframes spin { to { transform: rotate(360deg); } }
      .input-error { border: 1px solid #ef4444 !important; box-shadow: 0 0 0 3px rgba(239,68,68,.15); }
      .error-text { color: #ef4444; font-size: 12px; margin-top: 6px; }
      .modal-title { font-weight: 800; font-size: 18px; }
      .modal-subtitle { color: var(--muted); font-size: 13px; margin-top: 4px; }
      .field-label { font-weight: 700; font-size: 13px; margin-bottom: 6px; display:block; }
      .field { width: 100%; padding: 10px 12px; border: 1px solid var(--line); border-radius: 10px; background: var(--card); color: var(--text); }
      .field:focus { outline: none; box-shadow: 0 0 0 3px rgba(37,99,235,.15); border-color: var(--primary); }
      .modal-card { width: 680px; max-width: calc(100vw - 32px); background: var(--card); border-radius: 16px; box-shadow: var(--shadow); }
      .modal-card--form { width: min(680px, calc(100vw - 32px)); max-height: calc(100vh - 32px); display: flex; flex-direction: column; overflow: hidden; padding: 0 !important; }
      .modal-actions { display:flex; gap: 10px; justify-content: flex-end; padding-top: 6px; }
      .btn-secondary { border: 1px solid var(--line); background: #f3f4f6; color: var(--text); border-radius: 10px; padding: 10px 14px; font-weight: 600; cursor: pointer; }
      .btn-secondary:hover { background: #e5e7eb; }
      .section-split { height: 1px; background: var(--line); margin: 12px 0; }
      .two-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .toggle-btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
      .toggle-ativo { background: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; }
      .toggle-inativo { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }
      .especialidade-select::-ms-expand { display: none; }
      .especialidade-select {
        background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: right 10px center;
        background-size: 20px;
        padding-right: 36px;
        appearance: none;
        cursor: pointer;
        transition: all 0.2s;
      }
      .especialidade-select:focus {
        border-color: var(--primary) !important;
        box-shadow: 0 0 0 3px rgba(37,99,235,.15), inset 0 1px 3px rgba(0,0,0,0.05) !important;
      }
      .especialidade-select:hover:not(:disabled) {
        border-color: var(--primary) !important;
        box-shadow: 0 2px 12px rgba(37,99,235,0.10) !important;
      }
      .especialidade-select:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      .especialidade-select option {
        background: #f3f4f6;
        color: #222;
        font-size: 14px;
        padding: 10px 16px;
        border-bottom: 1px solid #e5e7eb;
        font-weight: 500;
      }
      .especialidade-select option:checked {
        background: #2563eb;
        color: white;
        font-weight: 600;
      }
      .especialidade-select option:hover {
        background: #e5e7eb;
        color: #222;
      }
      .especialidade-select option[value=""] {
        color: #999;
        font-style: italic;
      }
      @media (max-width: 768px) {
        .two-cols { grid-template-columns: 1fr; }
        .modal-actions { flex-direction: column-reverse; }
        .modal-actions > * { width: 100%; }
        .modal-card--form { width: 100%; max-width: 100%; height: 100%; max-height: 100vh; border-radius: 0; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Carregar especialidades ao abrir o Drawer de edição
  useEffect(() => {
    if (drawerOpen && profissionalSelecionado?.estabelecimento_id) {
      getEspecialidadesPorEstabelecimento(profissionalSelecionado.estabelecimento_id)
        .then(setEspecialidades)
        .catch(() => setEspecialidades([]));
    }
  }, [drawerOpen, profissionalSelecionado?.estabelecimento_id]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.body.classList.contains("dark");
      setTheme(isDark ? "dark" : "light");
    });
    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const carregarProfissionais = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = {
        page: pagina,
        limit: itensPorPagina,
        busca,
        status: statusFiltro !== 'todos' ? statusFiltro : undefined,
      };
      // ... requisição usando params ...
      // Exemplo:
      const { data } = await api.get('/profissionais', { params });
      setProfissionais(data.resultados || []);
      setTotalPaginas(data.paginas || 1);
    } catch (err) {
      console.error('Erro ao carregar profissionais:', err);
      if (!silent) toast.error('Erro ao carregar profissionais!');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [pagina, itensPorPagina, busca, statusFiltro]);

  useEffect(() => {
    carregarProfissionais(false);
  }, [pagina, itensPorPagina, busca, statusFiltro, carregarProfissionais]);

  useEffect(() => {
    const handler = () => carregarProfissionais(true);
    window.addEventListener("fz:agendamento", handler);
    window.addEventListener("fz:notificacao", handler);
    return () => {
      window.removeEventListener("fz:agendamento", handler);
      window.removeEventListener("fz:notificacao", handler);
    };
  }, [carregarProfissionais]);

  useAutoRefresh(
    () => carregarProfissionais(true),
    10000,
    !drawerOpen && !showNovo && !optsLoading && !novoLoading
  );

  // Não precisa filtrar localmente, pois já vem filtrado do backend
  const listaPaginada = profissionais;
  const paginationItems = buildPaginationItems(pagina, totalPaginas);

const getEstabelecimentoPadrao = () => {
  const est = localStorage.getItem('estabelecimento_id');
  return est || "";
};

  // ========== NOVO PROFISSIONAL ==========
const abrirModalNovo = async () => {
  setErros({});
  setOptsLoading(true);
  await carregarOpcoesSelects(); // Aguarda o carregamento dos estabelecimentos
  setOptsLoading(false);
  const estabelecimentoPadrao = getEstabelecimentoPadrao();
  setNovoProfissional({
    nome: "",
    telefone: "",
    admin: false,
    estabelecimento_id: estabelecimentoPadrao,
    especialidades: [],
    foto: null,
    ativo: true,
  });
  setShowNovo(true);
  // Carregar especialidades do estabelecimento padrão
  if (estabelecimentoPadrao) {
    try {
      const especialidadesData = await getEspecialidadesPorEstabelecimento(estabelecimentoPadrao);
      setEspecialidades(especialidadesData);
    } catch (err) {
      setEspecialidades([]);
      console.error('Erro ao buscar especialidades:', err);
    }
  } else {
    setEspecialidades([]);
  }
};

  const fecharModalNovo = () => {
    setShowNovo(false);
  };

  const normalizaResultado = (data) =>
    Array.isArray(data) ? data : data?.resultados || [];

  const carregarOpcoesSelects = async () => {
    try {
      setOptsLoading(true);
      const estRes = await api.get("/estabelecimentos");
      setEstabelecimentos(normalizaResultado(estRes.data));
    } catch (e) {
      console.error("Erro ao carregar opções:", e);
      toast.error("Erro ao carregar opções.");
    } finally {
      setOptsLoading(false);
    }
  };

  const validarNovo = () => {
  const e = {};
  if (!novoProfissional.nome)
    e.nome = `Digite o nome ${rotulosProfissional.de} ${rotulosProfissional.singularLower}`;
  if (!novoProfissional.estabelecimento_id)
    e.estabelecimento_id = "Selecione um estabelecimento";
  setErros(e);
  return Object.keys(e).length === 0;
};

  const salvarNovoProfissional = async () => {
    if (!validarNovo()) return;
    setNovoLoading(true);
    try {
      const formData = new FormData();
      formData.append('nome', novoProfissional.nome);
      const telefoneDigits = novoProfissional.telefone ? String(novoProfissional.telefone).replace(/\D/g, '') : '';
      formData.append('telefone', telefoneDigits);
      formData.append('admin', !!novoProfissional.admin);
      formData.append('estabelecimento_id', Number(novoProfissional.estabelecimento_id));
      formData.append('especialidades', JSON.stringify(novoProfissional.especialidades));
      formData.append('ativo', !!novoProfissional.ativo);
      if (novoProfissional.foto) {
        formData.append('foto', novoProfissional.foto);
      }
      const { data } = await api.post("/profissionais", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(`${rotulosProfissional.singular} ${rotulosProfissional.criado} com sucesso!`);
      if (data?.adminAnterior?.length) {
        const nomes = data.adminAnterior.map((a) => a.nome).filter(Boolean).join(', ');
        toast.info(`Admin anterior desmarcado: ${nomes || `${rotulosProfissional.outro} ${rotulosProfissional.singularLower}`}`);
      }
      setShowNovo(false);
      carregarProfissionais();
    } catch (e) {
      const code = e.response?.data?.code;
      const mensagem = e.response?.data?.mensagem || `Erro ao criar ${rotulosProfissional.singularLower}.`;
      if (code === "ASSINATURA_SOMENTE_LEITURA") {
        toast.warning(mensagem);
      } else if (code === "LIMITE_PROFISSIONAIS") {
        const result = await Swal.fire({
          icon: "info",
          title: "Limite do plano atingido",
          text: mensagem,
          showCancelButton: true,
          confirmButtonText: "Ver planos",
          cancelButtonText: "Fechar",
        });
        if (result.isConfirmed) {
          window.location.href = "/assinatura";
        }
      } else if (e.response?.status === 402) {
        toast.warning(mensagem);
      } else {
        console.error("Erro ao criar profissional:", e);
        toast.error(mensagem);
      }
    } finally {
      setNovoLoading(false);
    }
  };

  const renderProfissionalInfo = (prof) => (
    <div className="entity-cell entity-cell--with-avatar">
      <span className="entity-avatar">{getInitials(prof.nome)}</span>
      <div className="entity-cell">
        <span className="entity-cell__title">{prof.nome || "-"}</span>
        <span className="entity-cell__subtitle">
          {prof.estabelecimento?.nome || "Sem estabelecimento vinculado"}
        </span>
      </div>
    </div>
  );

  const renderProfissionalAcoes = (prof) => (
    <div className="table-actions table-actions--compact">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        className="action-icon-btn action-icon-btn--primary"
        onClick={() => abrirDrawer(prof)}
        title={`Editar ${rotulosProfissional.singularLower}`}
      >
        <Edit size={18} /> <span className="action-label">Editar</span>
      </motion.button>
      {!prof.usuario_id ? (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          className="action-icon-btn action-icon-btn--danger"
          onClick={() => confirmarExcluirProfissional(prof.id, prof.nome || "")}
          title={`Excluir ${rotulosProfissional.singularLower}`}
        >
          <Trash2 size={18} /> <span className="action-label">Excluir</span>
        </motion.button>
      ) : (
        <span
          className="muted"
          title="Vinculado ao seu perfil — não pode ser excluído"
          style={{ fontSize: 12, alignSelf: "center" }}
        >
          Vinculado ao perfil
        </span>
      )}
    </div>
  );

  return (
    <div className="container fade-in">
      <ToastContainer
        position="top-right"
        theme={theme === "dark" ? "dark" : "colored"}
      />

      <div
        className="section-title flex items-center gap-2"
        style={{ justifyContent: "space-between" }}
      >
        <div className="flex items-center gap-2">
          <Stethoscope /> <span>{rotulosProfissional.plural}</span>
        </div>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          className="btn"
          onClick={abrirModalNovo}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            borderRadius: 10,
            fontWeight: 700,
          }}
        >
          <Plus /> {rotulosProfissional.novo} {rotulosProfissional.singular}
        </motion.button>
      </div>

      {/* FILTROS */}
      <motion.div
        className="card-block space-b management-filters"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="management-filters__title">Filtrar {rotulosProfissional.pluralLower}</div>
        <div className="filters-row">
          <div
            className="flex items-center filter-field"
            style={{
              background: "var(--card)",
              border: "1px solid var(--line)",
              borderRadius: "10px",
              padding: "6px 10px",
              boxShadow: "var(--shadow)",
            }}
          >
            <Search style={{ color: "var(--muted)", marginRight: "8px" }} />
            <input
              type="text"
              placeholder="Buscar por nome, telefone, especialidade..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              style={{
                width: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--text)",
              }}
            />
          </div>

          <CustomSelect
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value)}
            options={[
              { value: "todos", label: "Todos os status" },
              { value: "ativo", label: "Ativo" },
              { value: "inativo", label: "Inativo" },
            ]}
            style={{ minWidth: "180px" }}
          />
          <CustomSelect
            value={itensPorPagina}
            onChange={(e) => {
              setPagina(1);
              setItensPorPagina(Number(e.target.value));
            }}
            options={[
              { value: 5, label: "5 por página" },
              { value: 10, label: "10 por página" },
              { value: 20, label: "20 por página" },
            ]}
            style={{ minWidth: "150px" }}
          />
        </div>
      </motion.div>

      {/* LISTAGEM */}
      <motion.div
        className="card-block"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {loading ? (
          <p
            className="muted"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <Spinner /> Carregando {rotulosProfissional.pluralLower}...
          </p>
        ) : listaPaginada.length === 0 ? (
          <div className="empty-state empty-state--compact">
            <Stethoscope size={34} />
            <strong>{rotulosProfissional.nenhum} {rotulosProfissional.singularLower} {rotulosProfissional.encontrado}</strong>
            <span>Cadastre {rotulosProfissional.pluralLower} para disponibilizar horários na agenda.</span>
          </div>
        ) : (
          <>
            <div className="management-mobile-list">
              <div className="management-mobile-list__head">
                <span>{rotulosProfissional.singular}</span>
                <span>Ações</span>
              </div>
              {listaPaginada.map((prof) => (
                <div key={prof.id} className="management-mobile-row">
                  <div className="management-mobile-row__main">{renderProfissionalInfo(prof)}</div>
                  <div className="management-mobile-row__actions">{renderProfissionalAcoes(prof)}</div>
                </div>
              ))}
            </div>

            <div className="management-table-desktop table-responsive">
              <table className="table table--mobile-actions">
                <thead>
                  <tr>
                    <th>{rotulosProfissional.singular}</th>
                    <th>Telefone</th>
                    <th>Perfil</th>
                    <th>Status</th>
                    <th className="management-actions-cell">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {listaPaginada.map((prof) => (
                    <motion.tr
                      key={prof.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <td>{renderProfissionalInfo(prof)}</td>
                      <td>{prof.telefone ? formatTelefone(prof.telefone) : <span className="muted">Não informado</span>}</td>
                      <td>
                        <span className="meta-pill">{prof.admin ? "Administrador" : rotulosProfissional.singular}</span>
                      </td>
                      <td>
                        <span className={`status-pill ${isAtivo(prof) ? "status-pill--active" : "status-pill--inactive"}`}>
                          {isAtivo(prof) ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="management-actions-cell">{renderProfissionalAcoes(prof)}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Paginação - sempre exibe os botões se houver mais de 1 item */}
            {listaPaginada.length > 0 && (
              <div className="pagination-bar pagination-bar--spaced">
                <button
                  className="btn-secondary"
                  disabled={pagina === 1}
                  onClick={() => setPagina(pagina - 1)}
                  style={{ minWidth: 60, borderRadius: 8, fontWeight: 600, fontSize: 15, opacity: pagina === 1 ? 0.6 : 1 }}
                >Anterior</button>
                {paginationItems.map((item, index) => {
                  if (typeof item !== "number") {
                    return (
                      <span key={`${item}-${index}`} className="pagination-ellipsis" aria-hidden="true">
                        ...
                      </span>
                    );
                  }
                  return (
                    <button
                      key={item}
                      className={`btn-secondary${pagina === item ? ' active' : ''}`}
                      style={{
                        minWidth: 40,
                        borderRadius: 8,
                        fontWeight: pagina === item ? 700 : 500,
                        fontSize: 15,
                        background: pagina === item ? 'var(--primary-light)' : undefined,
                        color: pagina === item ? 'var(--primary)' : undefined,
                        boxShadow: pagina === item ? '0 2px 8px rgba(37,99,235,0.08)' : undefined,
                        border: pagina === item ? '2px solid var(--primary)' : '1px solid var(--line)',
                        transition: 'all 0.2s',
                      }}
                      onClick={() => setPagina(item)}
                    >
                      {item}
                    </button>
                  );
                })}
                <button
                  className="btn-secondary"
                  disabled={pagina === totalPaginas}
                  onClick={() => setPagina(pagina + 1)}
                  style={{ minWidth: 60, borderRadius: 8, fontWeight: 600, fontSize: 15, opacity: pagina === totalPaginas ? 0.6 : 1 }}
                >Próxima</button>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* DRAWER (edição) */}
      <AnimatePresence>
        {drawerOpen && profissionalSelecionado && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={fecharDrawer}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.4)",
                backdropFilter: "blur(4px)",
                zIndex: 50,
              }}
            />
            <motion.div
              className="drawer-panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 280, damping: 30 }}
              style={{ zIndex: 60 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <h3 style={{ fontWeight: "700", fontSize: "20px" }}>
                  Editar {rotulosProfissional.singular}
                </h3>
                <button
                  onClick={fecharDrawer}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--muted)",
                    fontSize: "18px",
                  }}
                >
                  <X />
                </button>
              </div>

              {profissionalSelecionado.usuario_id ? (
                <p className="muted" style={{ marginTop: 12, marginBottom: 0, fontSize: 13, lineHeight: 1.4 }}>
                  {rotulosProfissional.este} {rotulosProfissional.singularLower} está {rotulosProfissional.vinculado} ao usuário do painel. Nome e telefone ficam sincronizados com{" "}
                  <Link to="/perfil" style={{ fontWeight: 700 }}>Meu perfil</Link>.
                </p>
              ) : null}

              <div style={{ marginTop: "20px" }}>
                <label className="field-label">Nome {rotulosProfissional.de} {rotulosProfissional.singular}</label>
                <input
                  type="text"
                  className="field"
                  value={profissionalSelecionado.nome || ""}
                  onChange={(e) =>
                    setProfissionalSelecionado({
                      ...profissionalSelecionado,
                      nome: e.target.value,
                    })
                  }
                  placeholder="Digite o nome"
                  style={{ marginBottom: 12 }}
                />

                <label className="field-label" style={{ marginTop: 12 }}>Telefone</label>
                <input
                  type="text"
                  className="field"
                  value={profissionalSelecionado.telefone || ""}
                  onChange={(e) => {
                    const masked = formatTelefone(e.target.value);
                    setProfissionalSelecionado({
                      ...profissionalSelecionado,
                      telefone: masked,
                    });
                  }}
                  placeholder="Digite o telefone"
                  style={{ marginBottom: 12 }}
                />

                <div style={{ marginTop: 8, marginBottom: 12 }}>
                  <div className="field-label">Admin do estabelecimento</div>
                  <button
                    type="button"
                    aria-pressed={!!profissionalSelecionado.admin}
                    className={`toggle-btn ${profissionalSelecionado.admin ? 'toggle-ativo' : 'toggle-inativo'}`}
                    onClick={() =>
                      setProfissionalSelecionado({
                        ...profissionalSelecionado,
                        admin: !profissionalSelecionado.admin,
                      })
                    }
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                  >
                    {profissionalSelecionado.admin ? <ToggleRight /> : <ToggleLeft />}
                    <span style={{ fontWeight: 700 }}>{profissionalSelecionado.admin ? 'Admin' : 'Não'}</span>
                  </button>
                  <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                    Se marcar, {rotulosProfissional.este.toLowerCase()} {rotulosProfissional.singularLower} se torna {rotulosProfissional.artigo} {rotulosProfissional.unico} admin do estabelecimento.
                  </div>
                </div>

                {SHOW_ESPECIALIDADES && (
                  <>
                    <label className="field-label">Especialidades</label>
                    <EspecialidadeTagSelect
                      especialidades={especialidades}
                      value={Array.isArray(profissionalSelecionado.especialidades) ? profissionalSelecionado.especialidades : []}
                      onAdd={(arr) => setProfissionalSelecionado({ ...profissionalSelecionado, especialidades: arr })}
                      onRemove={(arr) => setProfissionalSelecionado({ ...profissionalSelecionado, especialidades: arr })}
                      disabled={!profissionalSelecionado.estabelecimento_id}
                    />
                  </>
                )}

                <label className="field-label" style={{ marginTop: 12 }}>
                  Estabelecimento
                </label>
                <input
                  type="text"
                  className="field"
                  value={profissionalSelecionado.estabelecimento?.nome || "-"}
                  disabled
                  style={{ background: "#f3f4f6", cursor: "not-allowed", marginBottom: 12 }}
                />

                <div style={{ marginTop: 12 }}>
                  <div className="field-label">Ativo</div>
                  <button
                    type="button"
                    aria-pressed={!!profissionalSelecionado.ativo}
                    className={`toggle-btn ${profissionalSelecionado.ativo ? 'toggle-ativo' : 'toggle-inativo'}`}
                    onClick={() =>
                      setProfissionalSelecionado({
                        ...profissionalSelecionado,
                        ativo: !profissionalSelecionado.ativo,
                      })
                    }
                  >
                    {profissionalSelecionado.ativo ? <ToggleRight /> : <ToggleLeft />}
                    <span style={{ marginLeft: 6, fontWeight: 700 }}>{profissionalSelecionado.ativo ? 'Ativo' : 'Inativo'}</span>
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap", marginTop: "22px" }}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="btn hover-glow"
                  onClick={salvarAlteracoes}
                >
                  <Save style={{ marginRight: 6 }} />
                  Salvar Alterações
                </motion.button>
                <button className="btn-secondary" onClick={fecharDrawer}>
                  Fechar
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* MODAL NOVO PROFISSIONAL */}
      <AnimatePresence>
        {showNovo && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={fecharModalNovo}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.40)",
                backdropFilter: "blur(3px)",
                zIndex: 70,
              }}
            />
            <motion.div
              className="modal-shell"
              initial={{ scale: 0.98, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.98, opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
            >
              <div className="modal-card modal-card--form">
                <div className="modal-card__header">
                  <div>
                    <div className="modal-title">{rotulosProfissional.novo} {rotulosProfissional.singular}</div>
                    <div className="modal-subtitle">
                      Preencha os campos abaixo para cadastrar.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={fecharModalNovo}
                    className="modal-close-btn"
                    aria-label="Fechar"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="modal-card__scroll">
                {optsLoading ? (
                  <p
                    className="muted"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Spinner /> Carregando opções...
                  </p>
                ) : (
                  <div className="form-field-stack">
                    <div>
                      <label className="field-label">Nome {rotulosProfissional.de} {rotulosProfissional.singular} *</label>
                      <input
                        type="text"
                        className={`field${erros.nome ? " input-error" : ""}`}
                        value={novoProfissional.nome}
                        onChange={(e) => {
                          const nome = e.target.value;
                          setNovoProfissional((s) => ({ ...s, nome }));
                        }}
                        placeholder="Digite o nome"
                      />
                      {erros.nome && <div className="error-text">{erros.nome}</div>}
                    </div>

                    <div>
                      <label className="field-label">Telefone</label>
                      <input
                        type="text"
                        className="field"
                        value={novoProfissional.telefone}
                        onChange={(e) => {
                          const masked = formatTelefone(e.target.value);
                          setNovoProfissional((s) => ({ ...s, telefone: masked }));
                        }}
                        placeholder="Digite o telefone (opcional)"
                      />
                    </div>

                    <div>
                      <label className="field-label">Estabelecimento *</label>
                      <input
                        type="text"
                        className="field field-readonly"
                        value={(() => {
                          const est = estabelecimentos.find(
                            (e) => String(e.id) === String(novoProfissional.estabelecimento_id)
                          );
                          return est
                            ? est.nome || est.razao_social || `Estabelecimento #${est.id}`
                            : "";
                        })()}
                        disabled
                        readOnly
                      />
                    </div>

                    <div className="field-row-toggle">
                      <div className="field-row-toggle__copy">
                        <div className="field-label">Admin do estabelecimento</div>
                        <div className="muted">
                          Se marcar, {rotulosProfissional.este.toLowerCase()} {rotulosProfissional.singularLower} se torna {rotulosProfissional.artigo} {rotulosProfissional.unico} admin do estabelecimento.
                        </div>
                      </div>
                      <button
                        type="button"
                        aria-pressed={!!novoProfissional.admin}
                        className={`toggle-btn ${novoProfissional.admin ? "toggle-ativo" : "toggle-inativo"}`}
                        onClick={() =>
                          setNovoProfissional((s) => ({ ...s, admin: !s.admin }))
                        }
                      >
                        {novoProfissional.admin ? <ToggleRight /> : <ToggleLeft />}
                        <span style={{ fontWeight: 700 }}>
                          {novoProfissional.admin ? "Admin" : "Não"}
                        </span>
                      </button>
                    </div>

                    {SHOW_ESPECIALIDADES && (
                      <div>
                        <label className="field-label">Especialidades *</label>
                        <EspecialidadeTagSelect
                          especialidades={especialidades}
                          value={
                            Array.isArray(novoProfissional.especialidades)
                              ? novoProfissional.especialidades
                              : novoProfissional.especialidades
                                ? JSON.parse(novoProfissional.especialidades)
                                : []
                          }
                          onAdd={(arr) =>
                            setNovoProfissional((s) => ({ ...s, especialidades: arr }))
                          }
                          onRemove={(arr) =>
                            setNovoProfissional((s) => ({ ...s, especialidades: arr }))
                          }
                          disabled={!novoProfissional.estabelecimento_id}
                        />
                      </div>
                    )}

                    {SHOW_IMAGEM && (
                      <div>
                        <label className="field-label">Foto (imagem)</label>
                        <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                          <input
                            id="foto-upload"
                            type="file"
                            accept="image/*"
                            style={{
                              opacity: 0,
                              position: "absolute",
                              left: 0,
                              top: 0,
                              width: "100%",
                              height: "100%",
                              cursor: "pointer",
                            }}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              setNovoProfissional((s) => ({
                                ...s,
                                foto: file || null,
                                fotoPreview: file ? URL.createObjectURL(file) : null,
                              }));
                            }}
                          />
                          <label
                            htmlFor="foto-upload"
                            style={{
                              display: "block",
                              background: "var(--card)",
                              border: "1px solid var(--line)",
                              borderRadius: 10,
                              padding: "10px 16px",
                              fontWeight: 500,
                              color: "var(--text)",
                              boxShadow: "var(--shadow)",
                              cursor: "pointer",
                              transition: "background 0.2s, box-shadow 0.2s",
                              textAlign: "left",
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background = "var(--primary-light)";
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = "var(--card)";
                            }}
                          >
                            {novoProfissional.foto
                              ? novoProfissional.foto.name
                              : "Escolher imagem..."}
                          </label>
                        </div>
                        {novoProfissional.fotoPreview && (
                          <div
                            style={{
                              marginTop: 10,
                              display: "flex",
                              alignItems: "center",
                              gap: 16,
                              flexWrap: "wrap",
                            }}
                          >
                            <img
                              src={novoProfissional.fotoPreview}
                              alt="Preview"
                              style={{
                                maxWidth: "120px",
                                maxHeight: "120px",
                                borderRadius: 10,
                                boxShadow: "var(--shadow)",
                                border: "1px solid var(--line)",
                              }}
                            />
                            <button
                              type="button"
                              className="btn-secondary"
                              style={{ padding: "6px 14px", borderRadius: 8, fontWeight: 500 }}
                              onClick={() => {
                                setNovoProfissional((s) => ({
                                  ...s,
                                  foto: null,
                                  fotoPreview: null,
                                }));
                              }}
                            >
                              Remover imagem
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="field-row-toggle">
                      <div className="field-row-toggle__copy">
                        <div className="field-label">Ativo</div>
                        <div className="muted">{rotulosProfissional.singular} visível para agendamentos.</div>
                      </div>
                      <button
                        type="button"
                        aria-pressed={!!novoProfissional.ativo}
                        className={`toggle-btn ${novoProfissional.ativo ? "toggle-ativo" : "toggle-inativo"}`}
                        onClick={() =>
                          setNovoProfissional((s) => ({ ...s, ativo: !s.ativo }))
                        }
                      >
                        {novoProfissional.ativo ? <ToggleRight /> : <ToggleLeft />}
                        <span style={{ fontWeight: 700 }}>
                          {novoProfissional.ativo ? "Ativo" : "Inativo"}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
                </div>

                {!optsLoading && (
                  <div className="modal-card__footer modal-actions">
                    <button className="btn-secondary" onClick={fecharModalNovo}>
                      Cancelar
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="btn"
                      disabled={novoLoading}
                      onClick={salvarNovoProfissional}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {novoLoading ? <Spinner /> : <Plus />}
                      Criar {rotulosProfissional.singular}
                    </motion.button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Profissionais;
